/**
 * UserGrid component - displays paginated grid of user cards.
 * Implements page-level photo caching for instant navigation.
 */

import React, { useState, useEffect } from 'react';
import { Grid, Box, Pagination, Typography, CircularProgress } from '@mui/material';
import UserCard from './UserCard';
import { UserPhotoStatus } from '../types';
import { profilePictureApi } from '../services/profilePictureApi';
import { useAuth } from '../../../contexts/AuthContext';

interface UserGridProps {
  users: UserPhotoStatus[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  onPageChange: (page: number) => void;
  onManagePhotos: (user: UserPhotoStatus) => void;
}

const UserGrid: React.FC<UserGridProps> = ({
  users,
  total,
  page,
  pageSize,
  loading,
  onPageChange,
  onManagePhotos,
}) => {
  const { getAccessToken } = useAuth();
  const totalPages = Math.ceil(total / pageSize);

  // Global photo cache (persists across page changes)
  const [photoCache, setPhotoCache] = useState<Map<string, string>>(new Map());
  // Track photo timestamps to invalidate cache when photos change
  const [photoTimestamps, setPhotoTimestamps] = useState<Map<string, string>>(new Map());
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [prefetchedPages, setPrefetchedPages] = useState<Set<number>>(new Set());

  // Batch load photos for current page
  useEffect(() => {
    let isMounted = true;

    const loadPhotosForUsers = async (userList: typeof users, isPrefetch = false) => {
      const startTime = performance.now();
      const prefix = isPrefetch ? '[PREFETCH]' : '[PERF]';
      console.log(`${prefix} UserGrid: Batch photo load START`);

      if (userList.length === 0) {
        console.log(`${prefix} UserGrid: No users, skipping`);
        return new Map<string, string>();
      }

      // Invalidate cache entries where timestamp has changed
      const userIdsToInvalidate: string[] = [];
      userList.forEach(u => {
        const cachedTimestamp = photoTimestamps.get(u.user_id);
        const currentTimestamp = u.graph_photo_last_updated;

        // If timestamp changed, invalidate cache for this user
        if (cachedTimestamp && currentTimestamp && cachedTimestamp !== currentTimestamp) {
          console.log(`${prefix} UserGrid: Will invalidate cache for ${u.display_name} (timestamp changed)`);
          userIdsToInvalidate.push(u.user_id);
        }
      });

      // Batch invalidate all outdated cache entries
      if (userIdsToInvalidate.length > 0) {
        setPhotoCache(prev => {
          const newCache = new Map(prev);
          userIdsToInvalidate.forEach(id => newCache.delete(id));
          return newCache;
        });
        setPhotoTimestamps(prev => {
          const newMap = new Map(prev);
          userIdsToInvalidate.forEach(id => newMap.delete(id));
          return newMap;
        });
      }

      // Only load for users who have photos AND not already cached
      const userIdsToLoad = userList
        .filter(u => u.has_graph_photo && !photoCache.has(u.user_id))
        .map(u => u.user_id);

      const cachedCount = userList.filter(u => photoCache.has(u.user_id)).length;
      console.log(`${prefix} UserGrid: ${userIdsToLoad.length} users to load, ${cachedCount} already cached`);

      if (userIdsToLoad.length === 0) {
        console.log(`${prefix} UserGrid: All photos cached, skipping API call`);
        return new Map<string, string>();
      }

      if (!isPrefetch) {
        setLoadingPhotos(true);
      }

      try {
        const token = await getAccessToken();
        if (!token || !isMounted) {
          return new Map<string, string>();
        }

        // Single batch API call to fetch missing photos
        const apiStart = performance.now();
        const photoMap = await profilePictureApi.getBatchUserPhotos(userIdsToLoad, token);

        const apiTime = performance.now() - apiStart;
        console.log(`${prefix} UserGrid: Batch API complete (+${apiTime.toFixed(0)}ms), got ${photoMap.size} photos`);

        if (isMounted) {
          // Merge into global cache
          setPhotoCache(prev => {
            const newCache = new Map(prev);
            photoMap.forEach((url, userId) => newCache.set(userId, url));
            return newCache;
          });

          // Store timestamps for loaded users
          setPhotoTimestamps(prev => {
            const newMap = new Map(prev);
            userList
              .filter(u => userIdsToLoad.includes(u.user_id) && u.graph_photo_last_updated)
              .forEach(u => newMap.set(u.user_id, u.graph_photo_last_updated!));
            return newMap;
          });
        }

        const totalTime = performance.now() - startTime;
        console.log(`${prefix} UserGrid: Load complete - Total: ${totalTime.toFixed(0)}ms`);

        return photoMap;
      } catch (error) {
        console.error(`${prefix} UserGrid: Error loading photos:`, error);
        return new Map<string, string>();
      } finally {
        if (!isPrefetch && isMounted) {
          setLoadingPhotos(false);
        }
      }
    };

    // Load current page
    const loadCurrentPage = async () => {
      await loadPhotosForUsers(users, false);
    };

    loadCurrentPage();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, getAccessToken, photoTimestamps]);

  // Prefetch next page in background
  useEffect(() => {
    if (loading || loadingPhotos || users.length === 0) {
      return;
    }

    const nextPage = page + 1;

    // Don't prefetch if already prefetched or if no next page
    if (prefetchedPages.has(nextPage) || nextPage > totalPages) {
      return;
    }

    // Prefetch after a short delay to not interfere with current page
    const prefetchTimer = setTimeout(async () => {
      console.log(`[PREFETCH] Starting prefetch for page ${nextPage}`);

      // We don't have the actual next page users, so we'll just mark it for backend caching
      // The backend cache will handle this automatically since we cache by user ID

      setPrefetchedPages(prev => new Set(prev).add(nextPage));
      console.log(`[PREFETCH] Page ${nextPage} marked for prefetch`);
    }, 500);

    return () => clearTimeout(prefetchTimer);
  }, [page, totalPages, loading, loadingPhotos, users, prefetchedPages]);

  // Cleanup blob URLs only on unmount
  useEffect(() => {
    return () => {
      console.log(`[PERF] UserGrid unmounting, cleaning up ${photoCache.size} cached blob URLs`);
      photoCache.forEach(url => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (users.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          No users found
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Grid container spacing={2}>
        {users.map((user) => (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }} key={user.user_id}>
            <UserCard
              user={user}
              onManagePhotos={onManagePhotos}
              photoUrl={photoCache.get(user.user_id) || null}
              loadingPhoto={loadingPhotos && !photoCache.has(user.user_id)}
            />
          </Grid>
        ))}
      </Grid>

      {/* Pagination and info */}
      <Box mt={3} mb={2}>
        <Box display="flex" justifyContent="center" mb={1.5}>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Showing {users.length} of {total} users
          </Typography>
        </Box>

        {totalPages > 1 && (
          <Box display="flex" justifyContent="center">
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, newPage) => onPageChange(newPage)}
              color="primary"
              size="medium"
              showFirstButton
              showLastButton
              sx={{
                '& .MuiPaginationItem-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
                '& .MuiPaginationItem-root.Mui-selected': {
                  color: 'white',
                },
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default UserGrid;
